// src/writer/utils/bookImageSpecs.ts
export interface BookDimensions {
    width: number;
    height: number;
    spine?: number;
    dpi: number;
    aspectRatio: string;
}

export interface ImageMargins {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export interface BookImageSpecs {
    dimensions: BookDimensions;
    safeMargins: ImageMargins;
    barcodeArea?: {
        bottom: number;
        right: number;
        width: number;
        height: number;
    };
    recommendedColors: string[];
    typographyGuidelines: string[];
}

// Standard book format specifications at 300 DPI
export const BOOK_FORMAT_SPECS: Record<string, BookImageSpecs> = {
    '6x9': {
        dimensions: { width: 1800, height: 2700, spine: 100, dpi: 300, aspectRatio: '2:3' },
        safeMargins: { top: 150, bottom: 150, left: 150, right: 150 },
// FIX: Changed readonly string array to string[] for 'recommendedColors' and 'typographyGuidelines' in BookImageSpecs to resolve type incompatibility.
        recommendedColors: ['Muted tones', 'Professional blues and grays', 'Earth tones'],
        typographyGuidelines: ['Readable serif or sans-serif fonts', 'High contrast text', 'Minimum 12pt font size']
    },
    '5x8': {
        dimensions: { width: 1500, height: 2400, spine: 80, dpi: 300, aspectRatio: '5:8' },
        safeMargins: { top: 120, bottom: 120, left: 120, right: 120 },
        recommendedColors: ['Classic black and white', 'Single accent color', 'High contrast'],
        typographyGuidelines: ['Clean typography', 'Good spacing', 'Professional appearance']
    },
    '8.5x11': {
        dimensions: { width: 2550, height: 3300, spine: 120, dpi: 300, aspectRatio: '17:22' },
        safeMargins: { top: 180, bottom: 180, left: 180, right: 180 },
        recommendedColors: ['Technical blues and grays', 'Professional color scheme', 'Educational tones'],
        typographyGuidelines: ['Large readable fonts', 'Academic styling', 'Clear hierarchy']
    },
    'square': {
        dimensions: { width: 2400, height: 2400, spine: 100, dpi: 300, aspectRatio: '1:1' },
        safeMargins: { top: 150, bottom: 150, left: 150, right: 150 },
        recommendedColors: ['Artistic and creative', 'Bold color combinations', 'Expressive palettes'],
        typographyGuidelines: ['Creative typography', 'Artistic elements', 'Balanced composition']
    }
};

export type BookFormatKey = keyof typeof BOOK_FORMAT_SPECS;

// Image type specifications
export const IMAGE_TYPE_SPECS = {
    coverImage: {
        description: 'Front cover design for marketing and sales',
        requirements: [
            'Eye-catching and genre-appropriate design',
            'Clear title and author name placement',
            'Professional marketing appeal',
            'Thumbnail-friendly design'
        ],
        promptGuidelines: [
            'Include space for title overlay',
            'Genre-appropriate mood and style',
            'Professional publishing quality',
            'Avoid text in the image itself'
        ]
    },
    titlePage: {
        description: 'Interior title page with subtle design',
        requirements: [
            'Subtle and elegant design for inside the book',
            'Leave space for text overlay',
            'Complement the content style',
            'Print-friendly design'
        ],
        promptGuidelines: [
            'Muted colors or grayscale',
            'Subtle design elements',
            'Leave safe margins for text',
            'Interior page appropriate'
        ]
    },
    backCover: {
        description: 'Back cover with space for blurb and barcode',
        requirements: [
            'Leave space for book description',
            'Include barcode area',
            'Author photo placement',
            'ISBN and publisher info space'
        ],
        promptGuidelines: [
            'Minimal design with text areas',
            'Barcode placement area',
            'Author photo space',
            'Professional layout'
        ],
        barcodeArea: { bottom: 100, right: 100, width: 300, height: 150 }
    },
    authorPhoto: {
        description: 'Professional author headshot',
        requirements: [
            'High-quality portrait style',
            'Professional appearance',
            'Good lighting and composition',
            'Suitable for small reproduction'
        ],
        promptGuidelines: [
            'Professional headshot style',
            'Good lighting',
            'Neutral background',
            'Business appropriate'
        ]
    }
} as const;

export type ImageTypeKey = keyof typeof IMAGE_TYPE_SPECS;

// Helper functions
export const getBookFormatSpec = (format: BookFormatKey): BookImageSpecs => {
    return BOOK_FORMAT_SPECS[format];
};

export const getImageTypeSpec = (imageType: ImageTypeKey) => {
    return IMAGE_TYPE_SPECS[imageType];
};

export const getRecommendedFormat = (bookType: string): BookFormatKey => {
    switch (bookType.toLowerCase()) {
        case 'novel':
        case 'fiction':
            return '6x9';
        case 'poetry':
        case 'art book':
            return 'square';
        case 'textbook':
        case 'manual':
            return '8.5x11';
        case 'novella':
        case 'short stories':
            return '5x8';
        default:
            return '6x9';
    }
};

// Generate enhanced image prompt with specifications
export const getEnhancedImagePrompt = (
    imageType: ImageTypeKey,
    bookFormat: BookFormatKey,
    basePrompt: string,
    additionalOptions?: {
        designStyle?: 'minimalist' | 'ornate' | 'modern' | 'classic' | 'artistic';
        colorScheme?: 'monochrome' | 'duotone' | 'full-color' | 'warm' | 'cool';
        mood?: string;
    }
): string => {
    const formatSpec = getBookFormatSpec(bookFormat);
    const typeSpec = getImageTypeSpec(imageType);
    const { width, height, dpi } = formatSpec.dimensions;
    const { designStyle = 'appropriate to genre', colorScheme = 'professional', mood } = additionalOptions || {};

    const enhancedPrompt = `
Create a ${imageType} image for a book.

EXACT SPECIFICATIONS:
- Dimensions: ${width}x${height} pixels at ${dpi} DPI
- Aspect Ratio: ${formatSpec.dimensions.aspectRatio}
- Safe Margins: ${formatSpec.safeMargins.top}px on all sides
- Format: ${bookFormat} book format

IMAGE REQUIREMENTS:
${typeSpec.requirements.map(req => `- ${req}`).join('\n')}

DESIGN GUIDELINES:
${typeSpec.promptGuidelines.map(guide => `- ${guide}`).join('\n')}

STYLE SPECIFICATIONS:
- Design Style: ${designStyle}
- Color Scheme: ${colorScheme}
- Recommended Colors: ${formatSpec.recommendedColors.join(', ')}
- Typography Guidelines: ${formatSpec.typographyGuidelines.join(', ')}
${mood ? `- Mood: ${mood}` : ''}

BASE CONTENT:
${basePrompt}

TECHNICAL REQUIREMENTS:
- High resolution and print quality
- Professional publishing standards
- Suitable for both print and digital use
- ${imageType === 'backCover' ? 'Include designated barcode area in bottom right' : ''}
- Ensure proper contrast and readability
`.trim();

    return enhancedPrompt;
};

// Validate image dimensions
export const validateImageDimensions = (
    actualWidth: number,
    actualHeight: number,
    expectedFormat: BookFormatKey
): { isValid: boolean; message: string } => {
    const spec = getBookFormatSpec(expectedFormat);
    const { width: expectedWidth, height: expectedHeight } = spec.dimensions;
    
    const tolerance = 0.05; // 5% tolerance
    const widthDiff = Math.abs(actualWidth - expectedWidth) / expectedWidth;
    const heightDiff = Math.abs(actualHeight - expectedHeight) / expectedHeight;
    
    if (widthDiff <= tolerance && heightDiff <= tolerance) {
        return { isValid: true, message: 'Image dimensions are correct' };
    }
    
    return {
        isValid: false,
        message: `Image dimensions (${actualWidth}x${actualHeight}) don't match expected ${expectedFormat} format (${expectedWidth}x${expectedHeight})`
    };
};

// Get all available formats
export const getAvailableFormats = (): { key: BookFormatKey; label: string; description: string }[] => {
    return [
        { key: '6x9', label: '6" × 9"', description: 'Standard novel format (most popular)' },
        { key: '5x8', label: '5" × 8"', description: 'Compact format for novellas' },
        { key: '8.5x11', label: '8.5" × 11"', description: 'Textbook/manual format' },
        { key: 'square', label: 'Square', description: 'Art books and poetry' }
    ];
};